import type { DashboardCatalog } from './dashboard-uz-latn.ts';

/**
 * English dashboard texts. Uzbek is the default and the reference; this catalog exists so the
 * locale mechanism is real (the compiler and a test require every key in every locale) and for
 * demos and screenshots. Russian is added the same way later.
 */
export const dashboardEn: DashboardCatalog = {
  app: {
    name: 'Sales dashboard',
    skipToContent: 'Skip to main content',
    pageTitle: (page) => `${page} · Sales dashboard`,
  },
  demo: {
    banner: 'DEMO DATA — not real',
    notInSnapshotTitle: 'Not in the demo',
    notInSnapshot:
      'This demo shows only the preset periods (Today … This quarter), without filters.',
  },
  nav: {
    label: 'Main navigation',
    overview: 'Overview',
    funnel: 'Funnel',
    clients: 'Clients',
    team: 'Team',
    marketing: 'Marketing',
    status: 'Status',
    bottomLabel: 'Pages',
    more: 'More',
    short: {
      overview: 'Home',
      funnel: 'Funnel',
      clients: 'Clients',
      team: 'Team',
      marketing: 'Marketing',
      status: 'Status',
    },
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    back: 'Back',
    hideSidebar: 'Hide sidebar',
    showSidebar: 'Show sidebar',
    account: 'Account',
    themeToLight: 'Switch to light mode',
    themeToDark: 'Switch to dark mode',
    signOut: 'Sign out',
    signedInAs: (name) => `Signed in as ${name}`,
  },
  login: {
    title: 'Sign in',
    intro: 'Sign in with your account.',
    email: 'Login',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    invalid: 'Invalid email or password.',
    locked: (minutes) => `Too many failed attempts. Try again in ${minutes} minutes.`,
    rateLimited: 'Too many requests. Please try again shortly.',
    failed: 'Could not sign in. Please try again later.',
    expired: 'Your session has expired. Please sign in again.',
    otpLabel: 'Authenticator code',
    otpHint: 'Enter the 6-digit code from the authenticator app on your phone.',
    otpInvalid: 'The code or password is incorrect.',
  },
  security: {
    title: 'Security',
    navLabel: 'Security',
    intro: 'Your account security: password and two-step verification.',
    twoFactorIntro: 'Turn on two-step verification to protect your account.',
    twoFactorTitle: 'Two-step verification',
    enabled: 'Two-step verification is on.',
    enabledHint:
      'Signing in asks for an authenticator code as well as your password. If you lose your phone, contact an administrator.',
    disabled: 'Two-step verification is off.',
    start: 'Turn on',
    starting: 'Preparing…',
    step1:
      '1. In an authenticator app (Google Authenticator, Authy, …) add a new account and type in this key:',
    secretLabel: 'Key',
    step2: '2. Enter the 6-digit code the app shows:',
    codeLabel: 'Code',
    confirm: 'Confirm',
    confirming: 'Checking…',
    wrongCode: 'That code is not correct. Try again.',
    unavailable: 'Two-step verification is not set up on this server.',
    failed: 'That did not work. Please try again later.',
  },
  common: {
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    loading: 'Loading…',
    retry: 'Try again',
    reload: 'Reload page',
    close: 'Close',
    apply: 'Apply',
    reset: 'Clear',
    all: 'All',
    total: 'Total',
    noData: 'No data',
    dash: '—',
    notAvailable: 'Not available',
    unknown: 'Unknown',
    unassigned: 'Unassigned',
    notSpecified: 'Not specified',
    inactive: 'inactive',
    optional: 'optional',
    showTable: 'Show table',
    info: 'Info',
    sortBy: (column) => `Sort by ${column}`,
    dataTable: 'Data table',
    dismiss: 'Dismiss',
    chartTable: (title) => `${title}: data table`,
    offline: 'You are offline. Data will refresh when the connection returns.',
  },
  errors: {
    title: 'Could not load the data',
    detail: 'The request failed. Please try again.',
    requestId: (id) => `Request ID: ${id}`,
    forbiddenTitle: 'Access denied',
    forbiddenDetail: 'You do not have permission to view this page. Contact an administrator.',
    notFoundTitle: 'Page not found',
    notFoundDetail: 'This page does not exist or has moved.',
    backHome: 'Back to the home page',
    unknownSalesperson: 'No such salesperson.',
  },
  period: {
    label: 'Period',
    from: 'Start date',
    to: 'End date',
    presets: {
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      last7: 'Last 7 days',
      thisMonth: 'This month',
      last30: 'Last 30 days',
      lastMonth: 'Last month',
      thisQuarter: 'This quarter',
      custom: 'Custom',
    },
    invalid: 'The end date must not be before the start date.',
    tooLong: 'The period must not exceed 400 days.',
    range: (from, to) => (from === to ? from : `${from} – ${to}`),
  },
  compare: {
    label: 'Compare',
    previous_period: 'Previous period',
    previous_week: 'Same days last week',
    previous_year: 'Same period last year',
    none: 'No comparison',
    with: (range) => `Compared with: ${range}`,
    change: 'change',
    pp: 'pp',
    up: 'up',
    down: 'down',
    same: 'unchanged',
    noPrevious: 'No data for the comparison period',
    previous: (value) => `previous: ${value}`,
  },
  filters: {
    title: 'Filters',
    open: 'Open filters',
    salesperson: 'Salesperson',
    source: 'Source',
    clearAll: 'Clear all filters',
    active: 'Active filters',
    remove: (name) => `Remove filter ${name}`,
    none: 'None selected',
    selected: (count) => `${count} selected`,
    scopeNote: 'You can see only your own data.',
  },
  freshness: {
    asOf: (time) => `Data as of ${time}`,
    neverSynced: 'Never synchronised',
    fresh: 'fresh',
    delayed: 'delayed',
    stale: 'stale',
    demoNote: 'Demo data is generated on demand',
    staleBanner: 'The data may be out of date',
    label: 'Data freshness',
  },
  badge: { activity: 'Activity', cohort: 'Cohort', state: 'State' },
  badgeHelp: {
    activity: 'Events that happened in the selected period.',
    cohort: 'The group of leads created in the period, and where they stand today.',
    state: 'The situation right now (independent of the period).',
  },
  units: {
    leads: 'leads',
    deals: 'deals',
    containers: 'containers',
    containers_per_deal: 'per deal',
    visits: 'visits',
    tasks: 'tasks',
    ratio: '',
    minutes: 'min',
    hours: 'h',
    seconds: 's',
    calls: 'calls',
    money: '',
  },
  kpi: {
    leadsNew: {
      name: 'New leads',
      definition:
        'Leads that arrived in the period (by creation date). Spam, duplicates and other projects’ leads are excluded.',
    },
    leadsContacted: {
      name: 'Leads contacted',
      definition: 'Leads that received their first contact in the period. A lead counts once.',
    },
    leadsQualified: {
      name: 'Leads qualified',
      definition:
        'Leads marked qualified in the period. The qualification criteria are set by sales management.',
    },
    visitsPlanned: {
      name: 'Visits planned',
      definition:
        'Leads that moved to a “visit planned” stage in the period (leads, not visit records).',
    },
    visitsCompleted: {
      name: 'Visits completed',
      definition: 'Leads that moved to a “visit completed” stage in the period.',
      unavailable:
        'The CRM has no stage that says a visit took place, so this figure cannot be computed.',
    },
    dealsWon: {
      name: 'Deals won',
      definition: 'Leads won (sold) in the period, by the date they were won.',
    },
    containersSold: {
      name: 'Containers sold',
      definition: 'Containers in the deals won in the period. Never merged with deals.',
    },
    containersPerDeal: {
      name: 'Containers per deal',
      definition:
        'Containers sold divided by deals that have containers recorded. Deals without containers are left out.',
    },
    overallConversion: {
      name: 'Overall conversion',
      definition:
        'The share of leads created in the last 30 days that became deals (a cohort). It is not a ratio of one day’s events.',
      basis: (from, to, size) => `Cohort: ${from} – ${to}, ${size} leads`,
    },
  },
  immature: {
    title: 'The cohort is not mature yet',
    detail: (age, window) =>
      `The cohort is ${age} days old (maturity window: ${window} days). Recent leads have not had time to convert, so the percentages may grow later.`,
  },
  home: {
    sections: {
      sales: 'Sales',
      calls: 'Calls',
      ads: 'Advertising',
      more: 'More details',
    },
    sectionHints: {
      sales: 'Leads and sales in the selected period',
      calls: 'All inbound and outbound calls with customers',
      ads: 'Facebook / Instagram and Google advertising',
    },
    kpi: {
      conversionNote: (from: string, to: string) => `based on leads of ${from} – ${to}`,
      callsTotal: {
        name: 'Calls',
        definition:
          'All calls in the period: inbound and outbound, answered and not. Only calls of this project’s leads count.',
        split: (inbound: string, outbound: string) => `inbound ${inbound} · outbound ${outbound}`,
      },
      answerRate: {
        name: 'Answered',
        definition: 'Share of inbound calls a person picked up: answered ÷ all inbound calls.',
        missed: (count: string) => `${count} missed`,
      },
      avgWait: {
        name: 'Time to answer',
        definition:
          'Average seconds a caller waited until someone answered (answered inbound calls).',
        unavailable: 'The telephony system does not report waiting time.',
      },
      avgTalk: {
        name: 'Talk time',
        definition: 'Average duration of answered calls (inbound and outbound).',
      },
      missedNotReturned: {
        name: 'Missed, not returned',
        definition: (minutes: number) =>
          `Missed inbound calls that nobody returned or answered within ${minutes} minutes. Customers who may be lost.`,
        pending: (count: string) => `${count} more still waiting`,
      },
      spend: {
        name: 'Ad spend',
        definition:
          'Money spent on the ad platforms on the days of the period (Facebook / Instagram, Google).',
      },
      ctr: {
        name: 'CTR',
        definition: 'Share of people who saw an ad and clicked it: clicks ÷ impressions.',
      },
      cpl: {
        name: 'Cost per lead (CPL)',
        definition: 'Ad spend ÷ leads that came from ads. “—” when there were no leads.',
      },
      cac: {
        name: 'Cost per sale (CAC)',
        definition: 'Ad spend ÷ sales from this period’s ad leads.',
      },
      roas: {
        name: 'ROAS',
        definition:
          'Revenue per 1 dollar of ad spend: revenue ÷ spend. Below 1x the ads did not pay for themselves. Never negative.',
        lowerBound: 'Some sales have no amount — the real ROAS is higher.',
      },
    },
    funnel: {
      title: 'Funnel',
      subtitle: 'What happened to the leads that came in this period',
      lid: 'Leads',
      contacted: 'Contacted',
      won: 'Bought',
      lost: 'Not buying',
      open: 'In progress',
      notContacted: (count: string, share: string) => `not contacted: ${count} (${share})`,
      ofContacted: 'of contacted',
      conversion: (share: string) => `Lead to purchase: ${share}`,
      help: 'Leads created in the period, with their status today. “Not buying” is not a step before “Bought” but a separate outcome. The three outcomes add up to the contacted leads.',
      empty: 'No leads in this period.',
    },
    hours: {
      title: 'Calls by hour',
      answered: 'Answered',
      notAnswered: 'Not answered',
      hour: 'Hour',
      workHours: 'working hours',
      range: (hour: number) => `${hour}:00–${hour + 1}:00`,
      answerRate: (share: string) => `inbound answered: ${share}`,
      empty: 'No calls in this period.',
    },
    heatmap: {
      title: 'Calls by weekday and hour',
      subtitle: 'The darker the cell, the more calls at that time',
      metric: 'Measure',
      metrics: { calls: 'Calls', answerRate: 'Answered %', wait: 'Wait' },
      weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      weekdaysLong: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      perDay: (value: string) => `${value} per day on average`,
      busiest: (list: string) => `Busiest: ${list}`,
      low: 'fewer',
      high: 'more',
      tooFew: 'too few calls to tell',
      waitSeconds: (value: string) => `average wait ${value}`,
      answered: (share: string) => `answered ${share}`,
    },
    sources: {
      title: 'Lead sources',
      unknownShare: (share: string) =>
        `${share} of leads have no detected source: the CRM does not record it consistently. Source rules will fix this once configured.`,
      more: 'Details — Marketing',
      empty: 'No leads in this period.',
    },
    ads: {
      title: 'Ad performance',
      level: 'Level',
      levels: { campaign: 'Campaign', ad_set: 'Ad set', ad: 'Ad' },
      columns: {
        name: 'Name',
        spend: 'Spend',
        impressions: 'Impressions',
        clicks: 'Clicks',
        ctr: 'CTR',
        leads: 'Leads',
        cpl: 'CPL',
        sales: 'Sales',
        cac: 'CAC',
        revenue: 'Revenue',
        roas: 'ROAS',
      },
      total: 'Total',
      loss: 'loss',
      unattributed: (leads: string, sales: string) =>
        `Leads not linked to an ad: ${leads} (sales: ${sales}). They are not in the table.`,
      coverage: (share: string) =>
        `${share} of sales have an amount; revenue and ROAS are based on those.`,
      unknownLeads:
        'This campaign’s leads are linked at campaign level only and cannot be split by ad or ad set.',
      filtersIgnored:
        'Ad spend cannot be split by salesperson or source, so this section ignores the filters.',
      excluded: (list: string) => `Accounts in another currency are left out: ${list}.`,
      cohortNote: 'Leads and sales are this period’s leads; recent leads may not have bought yet.',
      chartTitle: 'CPL and CAC by ad set',
      chartNote: 'Each measure on its own scale. The 10 ad sets with the highest spend.',
      empty: 'No ad spend in this period.',
    },
  },
  overview: {
    title: 'Overview',
    leads: 'Leads',
    outcomes: 'Outcomes',
    conversion: 'Conversion',
    trend: 'Change over time',
    trendTooShortTitle: 'The period is too short to show a change',
    trendTooShort:
      'A one-day period has nothing to compare. Choose at least 2 days (for example “Last 7 days”).',
    trendMetric: 'Metric',
    containersByType: 'Containers by type',
    containersEmpty: 'No containers were sold in the selected period.',
    alerts: 'Needs attention',
    noAlerts: 'Nothing needs attention right now.',
    autoType: 'automatic type — an admin should review it',
  },
  trendMetrics: {
    leadsNew: 'New leads',
    leadsContacted: 'Contacted',
    leadsQualified: 'Qualified',
    visitsPlanned: 'Visits planned',
    visitsCompleted: 'Visits completed',
    dealsWon: 'Deals',
    containersSold: 'Containers',
    dealsLost: 'Lost deals',
  },
  granularity: { day: 'by day', week: 'by week', month: 'by month' },
  alerts: {
    FOLLOWUP_OVERDUE: 'Overdue follow-ups',
    LEADS_UNCONTACTED_AGING: 'Leads waiting for contact',
    DATA_STALE: 'Data is stale',
    DQ_LEAD_WITHOUT_SOURCE: 'Leads without a source',
    DQ_WON_WITHOUT_ITEMS: 'Won deals without containers',
    DQ_LOST_WITHOUT_REASON: 'Lost deals without a reason',
    DQ_UNMAPPED_STAGE: 'Leads in an unmapped stage',
    severity: { info: 'info', warning: 'warning', critical: 'critical' },
    open: 'Details',
  },
  funnel: {
    title: 'Sales funnel',
    mode: 'Mode',
    cohort: 'Cohort',
    activity: 'Activity',
    cohortHelp: 'Leads created in the period and where they stand today.',
    activityHelp: 'Events that happened in the period, whenever the lead was created.',
    activityNotice: 'This is not a cohort conversion',
    activityNoticeDetail:
      'Rates are not shown in activity mode: dividing one period’s events by each other is not a conversion rate.',
    steps: {
      NEW: 'New',
      CONTACTED: 'Contacted',
      QUALIFIED: 'Qualified',
      VISIT_SCHEDULED: 'Visit planned',
      VISIT_COMPLETED: 'Visit completed',
      WON: 'Won',
    },
    optionalStep: 'optional step',
    impliedNote: (count) => `${count} inferred from a later step`,
    columns: {
      step: 'Step',
      count: 'Count',
      ofCohort: 'Of cohort',
      fromPrevious: 'Of previous',
      median: 'Median time',
      p90: '90th percentile',
    },
    conversions: 'Step-to-step conversion',
    wonPaths: 'How the deals were won',
    wonPathsHelp:
      'The three groups do not overlap and add up to all deals won. A planned visit is not a completed visit.',
    afterCompletedVisit: 'After a completed visit',
    afterPlannedVisitOnly: 'After a planned visit only',
    withoutVisit: 'Without a visit',
    lost: 'Lost',
    open: 'In progress',
    visitsUnavailable:
      'The CRM has no stage that says a visit took place. Planned and completed visits are never merged.',
    losses: 'Loss reasons',
    lossesBasis: 'Basis',
    basisLostAt: 'By date lost',
    basisCohort: 'By cohort',
    furthest: 'Furthest step reached before the loss',
    timeToMilestone: 'Time to reach each step',
    emptyCohort: 'No leads were created in the selected period.',
    hours: (value) => `${value} h`,
  },
  clients: {
    title: 'Clients',
    caption: 'Client list',
    privacyNote:
      'Customer names and the purchase purpose are personal data: only users with permission can see them.',
    searchLabel: 'Search by customer name',
    searchPlaceholder: 'Name…',
    empty: 'No clients found in the selected period.',
    noName: 'No name recorded',
    columns: {
      date: 'Date',
      name: 'Client',
      source: 'Source',
      region: 'Region',
      purpose: 'Purchase purpose',
      containerSize: 'Container size',
      timeframe: 'Purchase timeframe',
      status: 'Status',
      manager: 'Manager',
    },
    outcome: { open: 'In progress', won: 'Bought', lost: 'Not buying' },
    range: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,
    previous: 'Previous',
    next: 'Next',
    pagination: 'Pages',
  },
  team: {
    title: 'Sales team',
    leaderboard: 'Results by salesperson',
    neutralNote: 'Ordered by containers, then deals. This is not a ranking.',
    calls: {
      title: 'Calls by salesperson',
      note: 'Only calls linked to a lead are counted. “Clients” are distinct clients with at least one answered call.',
      calls: 'Calls',
      answered: 'Answered',
      clients: 'Clients talked to',
      talkTime: 'Total talk time',
      avgTalk: 'Avg per answered call',
      totalsNote: 'Total counts a client once, even if several salespeople spoke to them.',
    },
    highlights: {
      title: 'Call highlights',
      note: 'Answered calls with a known duration. Open the call in the CRM to listen to the recording.',
      shortest: 'Shortest conversation',
      longest: 'Longest conversation',
      topClient: 'Talked to most often',
      teamTitle: 'Whole team',
      callsCount: (count: string) => `${count} calls`,
      talkTotal: (duration: string) => `${duration} in total`,
      lastCall: (when: string) => `last call ${when}`,
      noName: 'Client without a name',
      inbound: 'inbound',
      outbound: 'outbound',
      openInCrm: 'Open in CRM',
      callId: (id: string) => `Call ID: ${id}`,
      answered: 'Answered calls',
    },
    columns: {
      name: 'Salesperson',
      leadsAssigned: 'Leads assigned',
      leadsContacted: 'Contacted',
      leadsQualified: 'Qualified',
      visitsPlanned: 'Visits (planned)',
      visitsCompleted: 'Visits (completed)',
      dealsWon: 'Deals',
      containersSold: 'Containers',
      cohortConversion: 'Cohort conversion',
      firstResponse: 'First response (median)',
      followUpOnTime: 'Follow-ups on time',
      followUpsOverdue: 'Overdue',
    },
    attribution: {
      initial_owner: 'Credited to the owner when the lead was created.',
      owner_at_milestone: 'Credited to the owner when the step was reached.',
      owner_at_won: 'Credited to the owner when the deal was won.',
      task_owner: 'Credited to the task’s owner.',
    },
    unassigned: 'Unassigned',
    totals: 'Total (matches the overall figures)',
    detail: {
      metrics: 'Figures',
      funnel: 'This salesperson’s cohort',
      trend: 'Trend',
      overdue: 'Overdue follow-ups by age',
      reassignments: 'Lead reassignments',
      reassignedIn: 'Handed to this salesperson',
      reassignedOut: 'Handed away',
      ages: { lt1d: 'under 1 day', d1to3: '1–3 days', gt3d: 'over 3 days' },
    },
    empty: 'No salesperson data for the selected period.',
  },
  marketing: {
    title: 'Marketing performance',
    sources: 'By source',
    trend: 'New leads over time (by source)',
    mix: 'Source mix',
    unknownSource: 'Unknown source',
    columns: {
      source: 'Source',
      leadsNew: 'New leads',
      dealsWon: 'Deals',
      containersSold: 'Containers',
      cohortSize: 'Cohort (leads)',
      qualifiedRate: 'Qualified share',
      wonRate: 'Won share',
    },
    cohortPeriod: (range) => `Cohort period: ${range}`,
    notAvailable: 'Ad spend, impressions and clicks are not available from the CRM.',
    empty: 'No new leads in the selected period.',
  },
  status: {
    title: 'System status',
    dataSource: 'Data source',
    datasetKind: 'Dataset',
    demo: 'demo',
    live: 'live',
    freshness: 'Data freshness',
    entity: 'Entity',
    lastSuccess: 'Last success',
    failures: 'Consecutive failures',
    lastError: 'Last error',
    syncRuns: 'Latest synchronisations',
    run: {
      kind: 'Kind',
      status: 'Status',
      started: 'Started',
      finished: 'Finished',
      error: 'Error',
    },
    runStatus: { running: 'running', succeeded: 'succeeded', failed: 'failed' },
    worker: 'Background worker',
    workerUnknown: 'Not tracked yet',
    quality: 'Data quality',
    qualityHelp: 'Data-entry problems can distort the figures.',
    qualityItems: {
      leadsWithoutSource: 'Leads without a source',
      wonWithoutItems: 'Won deals without containers',
      wonWithoutCustomer: 'Won deals without a customer',
      lostWithoutReason: 'Lost deals without a reason',
      unmappedStage: 'Leads in an unmapped stage (now)',
      unknownContainerType: 'Containers of unknown type',
      autoContainerType: 'Containers with an automatically created type',
    },
    lineItems: (count, containers) => `${count} lines, ${containers} containers`,
  },
};
